import {useMemo} from 'react';

import type {ActionDefinitionObject} from '../types/ActionDefinition';
import type {FeGridSettings} from '../types/Api';
import type {RowData} from '../types/RowData';
import {getActionButtonSize} from '../utils/getActionButtonSize';
import {getActionColumnWidth} from '../utils/getActionColumnWidth';
import {BUTTON_SIZE_PX} from '../utils/lineHeightToValue';
import {partitionRowActions} from '../utils/partitionRowActions';

type UseGridActionColumnParams = {
  rows: RowData[];
  previousRows: RowData[];
  actionsDefinition: ActionDefinitionObject | null;
  gridSettings: FeGridSettings;
  actionColumnEnabled: boolean;
  isMobile: boolean;
};

type UseGridActionColumnReturn = {
  actionColumnWidth: number;
};

/** Fixed width for the action column on mobile — a single ⋮ button opens the action sheet. */
export const MOBILE_ACTION_COLUMN_WIDTH_PX = 40;

/**
 * Computes the action column width based on visible row actions.
 *
 * Iterates all rows to find the max icon count and whether overflow actions exist,
 * then calculates the required column width from button size and slot count.
 *
 * @mandatory Iterates all rows on every render. Memoizing prevents unnecessary
 * recomputation when unrelated state (loading, selection, column reorder) changes.
 * Used for action column width sizing — must be stable to avoid layout thrashing.
 */
export const useGridActionColumn = (
  params: UseGridActionColumnParams
): UseGridActionColumnReturn => {
  const buttonSizePx =
    BUTTON_SIZE_PX[getActionButtonSize(params.gridSettings.rowHeight, params.isMobile)];

  /**
   * Walk every visible row and ask partitionRowActions what would render. Desktop reserves
   * slots only for icon-backed primary/row-click actions plus the overflow trigger, matching
   * AG Grid. Mobile keeps a trigger for any action because its sheet renders text labels.
   */
  const {maxIconCount, hasOverflow, hasMobileActions} = useMemo(() => {
    const displayRows = params.rows.length > 0 ? params.rows : params.previousRows;
    const actionsDefinition = params.actionsDefinition ?? {};
    let maxIconCount = 0;
    let overflow = false;
    let mobileActions = false;

    for (const row of displayRows) {
      const {iconActions, overflowActions, rowClickAction} = partitionRowActions(
        row.actions,
        actionsDefinition
      );

      let rowIconCount = 0;
      const slotActions = rowClickAction ? [...iconActions, rowClickAction] : iconActions;
      for (const action of slotActions) {
        if (action.definition.icon.value) {
          rowIconCount += 1;
        }
      }
      if (rowIconCount > maxIconCount) {
        maxIconCount = rowIconCount;
      }
      if (overflowActions.length > 0) {
        overflow = true;
      }
      if (slotActions.length > 0 || overflowActions.length > 0) {
        mobileActions = true;
      }
    }

    return {maxIconCount, hasOverflow: overflow, hasMobileActions: mobileActions};
  }, [params.rows, params.previousRows, params.actionsDefinition]);

  const totalIconSlots = maxIconCount + (hasOverflow ? 1 : 0);
  const actionColumnWidth = !params.actionColumnEnabled // eslint-disable-line no-nested-ternary -- Pre-existing violation
    ? 0
    : params.isMobile // eslint-disable-line no-nested-ternary -- Pre-existing violation
      ? hasMobileActions
        ? MOBILE_ACTION_COLUMN_WIDTH_PX
        : 0
      : getActionColumnWidth(totalIconSlots, buttonSizePx);

  return {actionColumnWidth};
};
